import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../shared/api.js";
import { useFetch } from "../shared/useFetch.js";
import { Badge, Button, useToast } from "../ui/index.js";

type MatchDto = { id: number; name: string; date: string; score?: number };
type MatchLink = { dostatsId: number | null; current: MatchDto | null; candidates: MatchDto[] };

function label(m: MatchDto): string {
  const head = m.name.startsWith(m.date) ? m.name : `${m.date} ${m.name}`;
  return m.score !== undefined ? `${head} (${m.score.toFixed(2)})` : head;
}

/** Which dostats match the team stats come from — linked automatically,
 *  which can be wrong; the operator sees and changes it here. */
export function StatsApiLink({ matchId }: { matchId: string }) {
  const { toast } = useToast();
  const link = useFetch<MatchLink>(`/api/dostats/matches/${encodeURIComponent(matchId)}/match`);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  const data = link.data;
  useEffect(() => setChoice(data?.dostatsId ? String(data.dostatsId) : ""), [data?.dostatsId]);

  const err = link.error as (Error & { status?: number }) | null;
  if (err?.status === 503) {
    return <p className="text-[12px] text-ink-faint">dostats o'chiq — statistika faqat qo'lda.</p>;
  }
  if (!data) {
    return (
      <p className="text-[12px] text-ink-faint">
        {err ? `dostats javob bermadi: ${err.message}` : "dostats o'yini aniqlanmoqda…"}
      </p>
    );
  }

  const options = new Map<number, MatchDto>();
  if (data.current) options.set(data.current.id, data.current);
  for (const c of data.candidates) if (!options.has(c.id)) options.set(c.id, c);

  const save = async () => {
    const id = Number(choice);
    if (!id || id === data.dostatsId) return;
    setSaving(true);
    try {
      await api.put(`/api/dostats/matches/${encodeURIComponent(matchId)}/mapping`, {
        dostatsMatchId: id,
      });
      toast({
        tone: "success",
        title: "dostats o'yini bog'landi",
        description: "Statistika ~10 s ichida yangilanadi",
      });
      link.refetch();
    } catch (e) {
      toast({ tone: "error", title: "Saqlab bo'lmadi", description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-divider bg-surface-muted/40 px-3 py-2">
      <div className="flex items-center gap-2 text-[12px]">
        <span className="text-ink-muted">API manbasi:</span>
        {data.current ? (
          <span className="min-w-0 truncate font-medium text-ink">{label(data.current)}</span>
        ) : (
          <Badge tone="warning">bog'lanmagan</Badge>
        )}
        <Button
          variant="secondary"
          size="xs"
          className="ml-auto"
          onClick={() => link.refetch()}
          leadingIcon={<RefreshCw className={link.loading ? "h-3 w-3 animate-spin" : "h-3 w-3"} />}
        >
          Yangilash
        </Button>
      </div>
      {options.size > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            aria-label="dostats o'yini"
            className="h-7 min-w-0 flex-1 rounded-md border border-border bg-surface px-1.5 text-[12px] text-ink"
          >
            {!data.current && <option value="">— o'yinni tanlang —</option>}
            {[...options.values()].map((m) => (
              <option key={m.id} value={m.id}>
                {label(m)}
              </option>
            ))}
          </select>
          <Button
            size="xs"
            variant="secondary"
            disabled={saving || !choice || Number(choice) === data.dostatsId}
            onClick={() => void save()}
          >
            {saving ? "Saqlanmoqda…" : "Bog'lash"}
          </Button>
        </div>
      )}
    </div>
  );
}
